// src/pages/HandmadeGifts.js
import React, { useEffect, useRef, useState } from "react";

import "../styles/HandmadeGifts.css";
import Footer from "../components/Footer";

// Firestore
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

// Default page copy (used if Firestore is missing or empty)
const DEFAULT_PAGE = {
  heroTitle: "Crafted with Heart.\nWrapped in Thought.",
  heroSubtitle:
    "Turning Moments into Keepsakes – Custom handmade gifts that speak your story, made with love, details, and a dash of SuShe magic.",
  // If heroBackground is empty, no image (just CSS fallback color)
  heroBackground: "",
  hasWorkshop: false,
  workshopTitle: "Workshop",
  workshopDescription: "Something exciting is in the works... Stay tuned!",
  workshopCtaText: "Craft With Me",
  workshopCtaUrl: "",
  workshopImage: "",
};

const HandmadeGifts = () => {
  // Page content (hero + workshop block)
  const [page, setPage] = useState(DEFAULT_PAGE);

  // Gift cards (from Firestore)
  const [gifts, setGifts] = useState([]); // [{title,imageUrl,description,buyUrl,order,active}]
  const [loading, setLoading] = useState(true);

  // Horizontal scroller + arrows
  const cardScrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
    // Order enquiry modal
  const [selectedGift, setSelectedGift] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [contactNumber, setContactNumber] = useState("");
  const [requirements, setRequirements] = useState("");
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);

  // Load Firestore data
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // 1) Page content
        const pageRef = doc(db, "pageContent", "handmadeGifts");
        const pageSnap = await getDoc(pageRef);

        if (mounted && pageSnap.exists()) {
          const d = pageSnap.data() || {};
          setPage((prev) => ({
            ...prev,
            heroTitle: d.heroTitle || prev.heroTitle,
            heroSubtitle: d.heroSubtitle || prev.heroSubtitle,
            // Keep empty string if not provided (no image shown)
            heroBackground: d.heroBackground || "",
            hasWorkshop: Boolean(d.hasWorkshop),
            workshopTitle: d.workshopTitle || prev.workshopTitle,
            workshopDescription: d.workshopDescription || prev.workshopDescription,
            workshopCtaText: d.workshopCtaText || prev.workshopCtaText,
            workshopCtaUrl: d.workshopCtaUrl || prev.workshopCtaUrl,
            workshopImage: d.workshopImage || prev.workshopImage,
          }));
        }

        // 2) Gifts list
        const snap = await getDocs(collection(db, "handmadeGifts"));
        const items = [];
        snap.forEach((docu) => {
          const data = docu.data() || {};
          items.push({
            id: docu.id,
            title: data.title || "Untitled",
            imageUrl: data.imageUrl || "",
            description: data.description || "",
            buyUrl: data.buyUrl || "",
            order: typeof data.order === "number" ? data.order : 9999,
            active: data.active !== false, // default true
          });
        });

        const sorted = items.filter((g) => g.active).sort((a, b) => a.order - b.order);
        if (mounted) setGifts(sorted);
      } catch (err) {
        console.error("HandmadeGifts: failed to load data:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Decide when to show arrows (only if there are items AND overflow)
  useEffect(() => {
    const el = cardScrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 4;
      setShowArrows(gifts.length > 0 && hasOverflow);
    };

    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    const t = setTimeout(checkOverflow, 50);

    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [gifts]);

  const scrollCards = (direction) => {
    const el = cardScrollRef.current;
    if (!el) return;
    const scrollAmount = Math.min(360, Math.max(240, el.clientWidth * 0.85));
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

    const openOrderModal = (gift) => {
    setSelectedGift(gift);
    setCustomerName("");
    setCountryCode("+91");
    setContactNumber("");
    setRequirements("");
    setOrderSubmitted(false);
  };

  const closeOrderModal = () => {
    if (submittingOrder) return;

    setSelectedGift(null);
    setCustomerName("");
    setCountryCode("+91");
    setContactNumber("");
    setRequirements("");
    setOrderSubmitted(false);
  };

  const submitOrderEnquiry = async (e) => {
    e.preventDefault();

    if (!selectedGift) return;

        if (!customerName.trim() || !contactNumber.trim()) {
      return;
    }

    if (!/^[0-9]{7,15}$/.test(contactNumber.trim())) {
      alert("Please enter a valid contact number using digits only.");
      return;
    }

    try {
      setSubmittingOrder(true);

      await addDoc(collection(db, "handmadeGiftOrders"), {
        productId: selectedGift.id,
        productTitle: selectedGift.title,
        customerName: customerName.trim(),
        countryCode,
        contactNumber: contactNumber.trim(),
        requirements: requirements.trim(),
        status: "New",
        createdAt: serverTimestamp(),
      });

      setOrderSubmitted(true);
    } catch (err) {
      console.error("Failed to submit handmade gift enquiry:", err);
      alert("Failed to submit your enquiry. Please try again.");
    } finally {
      setSubmittingOrder(false);
    }
  };

  // Only apply a background image if the Firestore field is present
  const bgStyle = page.heroBackground ? { backgroundImage: `url(${page.heroBackground})` } : {};

  const lines = String(page.heroTitle || "").split("\n");
  const hasCta = page.hasWorkshop && !!page.workshopCtaUrl;

  return (
    <div className="handmade-container">
      {/* Hero Section */}
      <div className="handmade-hero" style={bgStyle}>
        <div className="handmade-hero-overlay" />
        <div className="handmade-hero-text">
          <h1>
            {lines.map((ln, i) => (
              <React.Fragment key={i}>
                {ln}
                {i < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
          <p>{page.heroSubtitle}</p>
        </div>
      </div>

      {/* Handmade With Love Section */}
      <div className="handmade-section">
        <h2 className="handmade-heading">Handmade With Love</h2>

        <div className="handmade-cards-wrapper">
          {showArrows && (
            <button
              type="button"
              className="scroll-arrow left"
              onClick={() => scrollCards("left")}
              aria-label="Scroll gifts left"
            >
              ‹
            </button>
          )}

          <div className="handmade-cards" ref={cardScrollRef}>
            {loading ? (
              <div className="no-handmade-message">Loading gifts…</div>
            ) : gifts.length > 0 ? (
              gifts.map((gift) => {
                const imgStyle = gift.imageUrl ? { backgroundImage: `url(${gift.imageUrl})` } : {};
                return (
                  <div key={gift.id} className="handmade-card">
                    <div
                      className={`gift-photo ${gift.imageUrl ? "" : "empty"}`}
                      style={imgStyle}
                      role="img"
                      aria-label={gift.imageUrl ? gift.title : "No image"}
                    />
                      <div className="gift-details">
                      <h3>{gift.title}</h3>
                      {gift.description && <p>{gift.description}</p>}

                      <button
                        type="button"
                        className="order-gift-button"
                        onClick={() => openOrderModal(gift)}
                      >
                        Order This Gift
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-handmade-message">
                ✂️ No handmade magic added here yet – stay tuned!
              </div>
            )}
          </div>

        {showArrows && (
            <button
              type="button"
              className="scroll-arrow right"
              onClick={() => scrollCards("right")}
              aria-label="Scroll gifts right"
            >
              ›
            </button>
          )}
        </div>

      </div>

      {selectedGift && (
        <div className="order-modal-overlay" onClick={closeOrderModal}>
          <div
            className="order-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="order-modal-close"
              onClick={closeOrderModal}
              aria-label="Close order enquiry"
            >
              ×
            </button>

            {!orderSubmitted ? (
              <>
                <h2>Order This Gift</h2>

                <p className="order-product-name">
                  {selectedGift.title}
                </p>

                <form onSubmit={submitOrderEnquiry}>
                  <label>
                    Your Name *
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      required
                    />
                  </label>

                                    <label>
                    Contact Number *
                    <div style={{ display: "flex", gap: "8px" }}>
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        style={{
                          width: "110px",
                          flex: "0 0 110px",
                        }}
                      >
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+61">🇦🇺 +61</option>
                        <option value="+64">🇳🇿 +64</option>
                        <option value="+971">🇦🇪 +971</option>
                        <option value="+65">🇸🇬 +65</option>
                        <option value="+60">🇲🇾 +60</option>
                        <option value="+49">🇩🇪 +49</option>
                        <option value="+33">🇫🇷 +33</option>
                        <option value="+39">🇮🇹 +39</option>
                        <option value="+34">🇪🇸 +34</option>
                        <option value="+81">🇯🇵 +81</option>
                        <option value="+82">🇰🇷 +82</option>
                        <option value="+86">🇨🇳 +86</option>
                        <option value="+852">🇭🇰 +852</option>
                        <option value="+974">🇶🇦 +974</option>
                        <option value="+966">🇸🇦 +966</option>
                        <option value="+27">🇿🇦 +27</option>
                      </select>

                      <input
                        type="tel"
                        value={contactNumber}
                        onChange={(e) =>
                          setContactNumber(
                            e.target.value.replace(/\D/g, "")
                          )
                        }
                        inputMode="numeric"
                        pattern="[0-9]{7,15}"
                        minLength={7}
                        maxLength={15}
                        placeholder="Enter phone number"
                        required
                      />
                    </div>
                  </label>

                  <label>
                    Customisation / Additional Requirements
                    <textarea
                      rows={4}
                      value={requirements}
                      onChange={(e) => setRequirements(e.target.value)}
                      placeholder="Tell me if you need any customisation or have any additional requirements."
                    />
                  </label>

                  <button
                    type="submit"
                    className="order-submit-button"
                    disabled={submittingOrder}
                  >
                    {submittingOrder
                      ? "Submitting..."
                      : "Submit Order Request"}
                  </button>
                </form>
              </>
            ) : (
              <div className="order-success">
                <h2>Thank You!</h2>

                <p>
                  Your order enquiry has been received. You will be
                  contacted within 24 hours to discuss further on your
                  order.
                </p>

                <button
                  type="button"
                  className="order-submit-button"
                  onClick={closeOrderModal}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section Divider */}
      <div className="section-divider" />

      {/* Workshop Section */}
      <div className="workshop-section">
        <h2 className="workshop-heading">{page.workshopTitle || "Workshop"}</h2>

        {page.hasWorkshop && page.workshopImage ? (
          <img
            src={page.workshopImage}
            alt="Workshop"
            className="workshop-image"
            loading="lazy"
          />
        ) : (
          <div className="workshop-placeholder">
            🎨 {page.workshopDescription || DEFAULT_PAGE.workshopDescription}
          </div>
        )}

        {hasCta ? (
          <a
            href={page.workshopCtaUrl}
            className="craft-button active"
            target="_blank"
            rel="noopener noreferrer"
          >
            {page.workshopCtaText || "Craft With Me"}
          </a>
        ) : (
          <button className="craft-button disabled" disabled>
            {page.workshopCtaText || "Craft With Me"}
          </button>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default HandmadeGifts;
